<app-loading-panel [show]="loading"></app-loading-panel>

@if (!loading) {
  <section class="pc-security-page">
    <header class="pc-security-header">
      <div class="pc-security-header__copy">
        <h2>Roles and permissions</h2>
        <p>Control what each role can view and manage across your company workspace.</p>
      </div>

      <div class="pc-security-header__actions">
        <button mat-stroked-button [disabled]="!dirty() || saving" (click)="load()">
          <mat-icon>undo</mat-icon>
          Discard
        </button>
        <button mat-raised-button color="primary" [disabled]="!dirty() || saving" (click)="saveClick()">
          @if (saving) {
            <mat-spinner diameter="18" strokeWidth="3"></mat-spinner>
          } @else {
            <mat-icon>save</mat-icon>
          }
          {{ saving ? 'Saving…' : 'Save changes' }}
        </button>
      </div>
    </header>

    @if (selectedRole) {
      <div class="pc-role-card">
        <div class="pc-role-card__identity">
          <span class="pc-role-card__icon"><mat-icon>admin_panel_settings</mat-icon></span>
          <div>
            <span class="pc-eyebrow">Selected role</span>
            <strong>{{ selectedRole.name }}</strong>
            <span>{{ selectedRole.powerUser ? 'Full application access' : (selectedRole.permissions?.length || 0) + ' permissions enabled' }}</span>
          </div>
        </div>

        <mat-form-field appearance="outline" class="pc-role-select">
          <mat-label>Role</mat-label>
          <mat-select [disabled]="dirty() || saving" [(ngModel)]="selectedRoleId" (selectionChange)="roleChange()">
            @for (role of roles; track role.id) {
              <mat-option [value]="role.id">{{role.name}}</mat-option>
            }
          </mat-select>
          @if (dirty()) {
            <mat-hint>Save or discard your changes before switching roles.</mat-hint>
          }
        </mat-form-field>

        <button mat-stroked-button class="pc-manage-role-button" [matMenuTriggerFor]="roleMenu">
          Manage role
          <mat-icon>expand_more</mat-icon>
        </button>

        <mat-menu #roleMenu="matMenu">
          <button mat-menu-item [disabled]="dirty()" (click)="add()">
            <mat-icon>add</mat-icon>
            <span>Add role</span>
          </button>
          <button mat-menu-item [disabled]="dirty()" (click)="copy()">
            <mat-icon>content_copy</mat-icon>
            <span>Duplicate role</span>
          </button>
          @if (hasMultipleCompanies) {
            <button mat-menu-item [disabled]="dirty()" (click)="migrateClick()">
              <mat-icon>drive_file_move</mat-icon>
              <span>Migrate roles from another company</span>
            </button>
          }
          <mat-divider></mat-divider>
          <button mat-menu-item class="pc-danger-action" [disabled]="dirty() || deleting" (click)="delete()">
            @if (deleting) {
              <mat-spinner diameter="20" strokeWidth="3"></mat-spinner>
            } @else {
              <mat-icon>delete_outline</mat-icon>
            }
            <span>{{ deleting ? 'Deleting…' : 'Delete role' }}</span>
          </button>
        </mat-menu>
      </div>

      <div class="pc-permissions-card">
        <div class="pc-permissions-card__heading">
          <div>
            <h3>Application permissions</h3>
            <p>Choose the areas and actions available to members assigned to this role.</p>
          </div>
          <span class="pc-permission-count">
            {{ selectedRole.permissions?.length || 0 }} of {{ permissionList.length }} enabled
          </span>
        </div>

        <div class="pc-power-user-row" [class.pc-power-user-row--active]="selectedRole.powerUser">
          <span class="pc-power-user-row__icon"><mat-icon>verified_user</mat-icon></span>
          <div class="pc-power-user-row__copy">
            <strong>Power User</strong>
            <span>Grant unrestricted access to every feature and permission in PlanaCan.</span>
          </div>
          <mat-slide-toggle
            [(ngModel)]="selectedRole.powerUser"
            (change)="powerUserChecked($event)"
            aria-label="Grant this role Power User access">
          </mat-slide-toggle>
        </div>

        @if (selectedRole.powerUser) {
          <div class="pc-power-user-note">
            <mat-icon>info_outline</mat-icon>
            Individual permissions are locked while Power User access is enabled.
          </div>
        }

        <mat-tree class="pc-permission-tree" [dataSource]="dataSource" [treeControl]="treeControl">
          <mat-tree-node *matTreeNodeDef="let node" matTreeNodeToggle matTreeNodePadding class="pc-permission-node pc-permission-node--leaf">
            <span class="pc-tree-spacer" aria-hidden="true"></span>
            <mat-checkbox
              class="checklist-leaf-node"
              [checked]="checklistSelection.isSelected(node)"
              [disabled]="selectedRole.powerUser"
              (change)="leafItemSelectionToggle(node)">
              {{node.item}}
            </mat-checkbox>
          </mat-tree-node>

          <mat-tree-node *matTreeNodeDef="let node; when: hasChild" matTreeNodePadding class="pc-permission-node pc-permission-node--group">
            <button mat-icon-button matTreeNodeToggle [attr.aria-label]="'Toggle ' + node.item">
              <mat-icon class="mat-icon-rtl-mirror">
                {{treeControl.isExpanded(node) ? 'expand_more' : 'chevron_right'}}
              </mat-icon>
            </button>
            <mat-checkbox
              [checked]="descendantsAllSelected(node)"
              [indeterminate]="descendantsPartiallySelected(node)"
              [disabled]="selectedRole.powerUser"
              (change)="itemSelectionToggle(node)">
              {{node.item}}
            </mat-checkbox>
          </mat-tree-node>
        </mat-tree>
      </div>
    } @else {
      <div class="pc-empty-state">
        <span class="pc-empty-state__icon"><mat-icon>admin_panel_settings</mat-icon></span>
        <h3>No roles configured</h3>
        <p>Create a role to begin assigning permissions to your team.</p>
        <button mat-raised-button color="primary" (click)="add()">
          <mat-icon>add</mat-icon>
          Add your first role
        </button>
      </div>
    }
  </section>
}
